import React, {useCallback, useMemo} from 'react';
import type {TypographyProps} from '@mui/material';
import {Checkbox, FormControlLabel} from '@mui/material';
import type {BaseTextFieldProps} from '@mui/material/TextField';
import {useImportantFont} from 'theme';
import {CheckboxCheckedIcon, CheckboxUncheckedIcon} from 'components/icons';
import type {BaseFieldProps} from './types';
import {Layout} from '../layout';
import './CheckboxField.scss';
import {FieldLabel} from './FieldLabel';

type Props = Omit<BaseFieldProps, 'label'> & {
  /** Label displayed above field */
  label?: string;
  /** Label displayed next to check box */
  boxLabel?: string;
  /** Unique id for field */
  fieldId: string;
  name: string;
  value?: boolean | null;
  /**
   * Font variant to use for option labels, default label
   */
  variant?: 'button';
  onChange?: (value: boolean) => void;
};

const checkboxSx = {padding: 0.75} as const;

export const CheckboxField: React.FC<Props> = ({
  label,
  boxLabel,
  fieldId,
  name,
  value,
  variant,
  readonly = false,
  onChange,
  layout,
  error,
}) => {
  const handleToggleValue = useCallback(() => {
    onChange && onChange(!value);
  }, [onChange, value]);
  const importantFont = useImportantFont(variant);

  const color: BaseTextFieldProps['color'] =
    error === 'warning' ? 'warning' : error === true ? 'error' : undefined;

  const componentProps = useMemo(
    () =>
      importantFont || color
        ? {
            typography: {
              ...(importantFont as TypographyProps | undefined),
              // Show validation state on box label
              ...(color ? {color: `${color}.main`} : undefined),
            },
          }
        : undefined,
    [color, importantFont],
  );

  return (
    <Layout adjustLeft {...layout}>
      {label && <FieldLabel label={label} htmlFor={fieldId} error={error} />}
      {boxLabel ? (
        <FormControlLabel
          label={boxLabel}
          htmlFor={fieldId}
          slotProps={componentProps}
          control={
            <Checkbox
              id={fieldId}
              name={name}
              checked={!!value}
              readOnly={readonly}
              disabled={readonly}
              onClick={handleToggleValue}
              icon={<CheckboxUncheckedIcon color={color} />}
              checkedIcon={<CheckboxCheckedIcon />}
              color={color}
              sx={checkboxSx}
            />
          }
        />
      ) : (
        <Checkbox
          id={fieldId}
          name={name}
          checked={!!value}
          readOnly={readonly}
          disabled={readonly}
          onClick={handleToggleValue}
          icon={<CheckboxUncheckedIcon color={color} />}
          checkedIcon={<CheckboxCheckedIcon />}
          sx={checkboxSx}
          color={color}
        />
      )}
    </Layout>
  );
};
